
<template>
  <div ref="dashboardTable" class="spl-dashboard-table">
    <table
      border="1"
      cellpadding="0"
      cellspacing="0">
      <thead>
        <tr>
          <th
            v-for="(header, index) in visibleFields"
            :key="'dashboardPrecariageHeader' + index"
            class="spl-dashboard-table__field"
            @click="toggleSort(header.name)">
            <span
              v-if="findSort(sortedFields, header.name)"
              :class="findSort(sortedFields, header.name).direction"
              class="order-arrow" />
            {{ header.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, rowIndex) in dashboardData" :key="'dashboardPrecariageField' + rowIndex">
          <td
            v-for="(field, fieldIndex) in visibleFields"
            :key="'dashboardPrecariageField' + rowIndex + '.' + fieldIndex"
            :class="[{'spl-dashboard-table__field--number': isNumber(row[field.name]),
                      'spl-dashboard-table__field--empty': isEmpty(row[field.name]),
                      'spl-dashboard-table__field--error': (field.isError && !isEmpty(row[field.name])) || isComplexError(field, row),
                      'spl-dashboard-table__field--warning': field.isWarning && !isEmpty(row[field.name])},
                     'spl-dashboard-table__field__'+field.name]"
            @click="elementSelected(row, field.name)">
            {{ formatField(row[field.name], field.name) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import constants from '@/constants'
import { find, findIndex, orderBy, isNumber, round } from 'lodash'
import { mapGetters } from 'vuex'

export default {
  props: {
    groupBy: {
      type: String,
      default: ''
    },
    listHeaderColumnGroups: {
      type: Array,
      required: true
    },
    parcelListExpanded: {
      type: Boolean,
      default: false
    },
    showOverview: {
      type: Boolean,
      required: true
    }
  },

  data () {
    return {
      dashboardData: [],
      visibleFields: [],
      sortedFields: [],
      fieldFilters: {},
      unsubscribeParcelFilter: null
    }
  },

  computed: {
    ...mapGetters('parcelFilter', ['parcelFilter']),
    defaultFields () {
      // Default Fields for the visible fields that are not selectable (customer and country are always given)
      const defaultFields = []
      const customer = { label: constants.DASHBOARD.CUSTOMER_NAME_LABEL, name: constants.DASHBOARD.CUSTOMER_NAME }
      const country = { label: constants.DASHBOARD.COUNTRY_NAME_LABEL, name: constants.DASHBOARD.COUNTRY_NAME }
      if (this.groupBy === constants.DASHBOARD.CUSTOMER_GROUP_BY) {
        defaultFields.push(customer)
        if (!this.showOverview) { defaultFields.push(country) }
      } else {
        defaultFields.push(country)
        if (!this.showOverview) { defaultFields.push(customer) }
      }
      return defaultFields
    }
  },
  watch: {
    listHeaderColumnGroups: {
      handler: function (newVal) {
        // Changes the visible fields when to user selects the fields
        this.buildVisibleFields()
      },
      deep: true
    },
    groupBy: function (newVal) {
      this.buildVisibleFields()
    },
    showOverview: function (newVal) {
      // First get the new visible fields (overview removes the second level of grouping)
      this.buildVisibleFields()
      // refresh the visible data
      if (this.originalDashboardData) {
        this.createPrecariageData(this.originalDashboardData)
      }
    }
  },
  beforeDestroy () {
    this.unsubscribeParcelFilter()
  },
  mounted () {
    // Manual linking fields to the fitting parcel statuses (for filtering / drill down from the list)
    this.fieldFilters = {}
    this.fieldFilters[constants.DASHBOARD.FIELD_TOTAL] = constants.PARCEL_STATUS.ALL
    this.fieldFilters[constants.DASHBOARD.FIELD_BOOKED] = constants.PARCEL_STATUS.BOOKED
    this.fieldFilters[constants.DASHBOARD.FIELD_NOT_BOOKED] = constants.PARCEL_STATUS.NOT_BOOKED
    this.fieldFilters[constants.DASHBOARD.FIELD_COLLECTED] = constants.PARCEL_STATUS.COLLECTED
    this.fieldFilters[constants.DASHBOARD.FIELD_NOT_COLLECTED] = constants.PARCEL_STATUS.NOT_COLLECTED
    this.fieldFilters[constants.DASHBOARD.FIELD_BOOKED_AND_COLLECTED] = constants.PARCEL_STATUS.BOOKED_AND_COLLECTED
    this.fieldFilters[constants.DASHBOARD.FIELD_IN_DESTINATION_COUNTRY] = constants.PARCEL_STATUS.IN_DESTINATION_COUNTRY
    this.fieldFilters[constants.DASHBOARD.FIELD_AT_PROVIDER] = constants.PARCEL_STATUS.AT_PROVIDER
    this.fieldFilters[constants.DASHBOARD.FIELD_IN_TRANSIT] = constants.PARCEL_STATUS.IN_TRANSIT
    this.fieldFilters[constants.DASHBOARD.FIELD_OUT_FOR_DELIVERY] = constants.PARCEL_STATUS.OUT_FOR_DELIVERY
    this.fieldFilters[constants.DASHBOARD.FIELD_DELIVERED] = constants.PARCEL_STATUS.DELIVERED
    this.fieldFilters[constants.DASHBOARD.FIELD_NOT_DELIVERED] = constants.PARCEL_STATUS.NOT_DELIVERED
    this.fieldFilters[constants.DASHBOARD.FIELD_ON_TIME] = constants.PARCEL_STATUS.ON_TIME
    this.fieldFilters[constants.DASHBOARD.FIELD_LATE] = constants.PARCEL_STATUS.LATE
    this.fieldFilters[constants.DASHBOARD.FIELD_EARLY] = constants.PARCEL_STATUS.EARLY
    this.fieldFilters[constants.DASHBOARD.FIELD_NOT_IN_DESTINATION_COUNTRY] = constants.PARCEL_STATUS.NOT_IN_DESTINATION_COUNTRY
    this.fieldFilters[constants.DASHBOARD.FIELD_MISSING_INFO] = constants.PARCEL_STATUS.MISSING_INFO
    this.fieldFilters[constants.DASHBOARD.FIELD_EXTERNAL_DELAY] = constants.PARCEL_STATUS.EXTERNAL_DELAY
    this.fieldFilters[constants.DASHBOARD.FIELD_HAS_REPORTED_EXCEPTION] = constants.PARCEL_STATUS.HAS_REPORTED_EXCEPTION
    this.unsubscribeParcelFilter = this.$store.subscribe(this.handleParcelFilterMutation)
    this.buildVisibleFields()
  },

  methods: {
    handleParcelFilterMutation (mutation, state) {
      if (mutation.type === 'parcelFilter/setParcelFilter') {
        this.buildVisibleFields()
      }
    },
    copyHTML () {
      window.addEventListener('copy', (event) => {
        event.preventDefault()
        event.clipboardData.setData('text/html', this.$refs.dashboardTable.innerHTML)
      })
      document.execCommand('copy')
    },
    // Creating the data from the backendData flatten it out
    createPrecariageData (data) {
      this.originalDashboardData = data
      this.dashboardData = []
      if (data.Dashboard_Header_List) {
        for (let i = 0; i < data.Dashboard_Header_List.length; i++) {
          const topElement = data.Dashboard_Header_List[i]
          if (this.showOverview) {
            this.dashboardData.push(topElement)
          } else {
            for (let j = 0; j < topElement.Dashboard_Detail_List.length; j++) {
              const element = topElement.Dashboard_Detail_List[j]
              this.dashboardData.push(element)
            }
          }
        }
      }
      this.sortData()
    },
    // Sorting the data
    sortData () {
      // Sort fields and directions seperate for lodash orderBy method
      let sortFields = []
      let sortDirections = []
      // Create the sort fields
      for (let i = 0; i < this.sortedFields.length; i++) {
        const sortElement = this.sortedFields[i]
        sortFields.push(sortElement.field)
        sortDirections.push(sortElement.direction)
      }
      // Default sorting depending on grouping
      if (sortFields.length === 0) {
        if (this.groupBy === constants.DASHBOARD.CUSTOMER_GROUP_BY) {
          sortFields = [data => data[constants.DASHBOARD.CUSTOMER_NAME].toLowerCase(), data => data[constants.DASHBOARD.COUNTRY_NAME].toLowerCase()]
        } else {
          sortFields = [data => data[constants.DASHBOARD.COUNTRY_NAME].toLowerCase(), data => data[constants.DASHBOARD.CUSTOMER_NAME].toLowerCase()]
        }
        sortDirections = ['asc', 'asc']
      }
      // Use the lodash orderBy method for sorting
      this.dashboardData = orderBy(this.dashboardData, sortFields, sortDirections)
    },
    // shows only selected fields from Field selector
    buildVisibleFields () {
      // Copies the defaultfields
      const tempVisibleFields = this.defaultFields.slice()
      // select fields from groups and put them in a flat hierarchy
      for (let i = 0; i < this.listHeaderColumnGroups.length; i++) {
        const group = this.listHeaderColumnGroups[i]
        for (let j = 0; j < group.columns.length; j++) {
          const column = group.columns[j]
          if ((this.parcelFilter('dashboard').Show_Forward_Flow && column.selected) ||
          (!this.parcelFilter('dashboard').Show_Forward_Flow && column.showInReturn && column.selected)) {
            tempVisibleFields.push({ label: column.label, name: column.name, isError: column.isError, isWarning: column.isWarning })
          }
        }
      }
      this.visibleFields = tempVisibleFields
    },
    // Special formatting for fields
    formatField (field, name) {
      // Weight & Picked up weight will be rounded to two decimal places then fixed in order to improve readability
      if (name === constants.DASHBOARD.FIELD_WEIGHT || name === constants.DASHBOARD.FIELD_COLLECTED_WEIGHT) {
        return round(field, 2).toFixed(2)
      } else if (name === constants.DASHBOARD.FIELD_VOLUME || name === constants.DASHBOARD.FIELD_COLLECTED_VOLUME) {
        // Volume will be rounded to 3 decimal places
        return round(field, 3).toFixed(3)
      } else if (name === constants.DASHBOARD.FIELD_NET_PERFORMANCE) {
        // Net Performance one decimal place and a percent sign
        return round(field, 1).toFixed(1) + '%'
      } else if (name === constants.DASHBOARD.FIELD_GROSS_PERFORMANCE) {
        // Gross Performance one decimal place and a percent sign
        return round(field, 1).toFixed(1) + '%'
      }
      // empty fields will be removed
      if (field === 0) {
        return ''
      }
      return field
    },
    // including of helper since lodash not available in vue template
    isNumber (value) {
      return isNumber(value)
    },
    // find the right sort value for displaying arrows
    findSort (array, value) {
      return find(array, { field: value })
    },
    // helper for removing 0 elements
    isEmpty (value) {
      return value === 0
    },
    // Special error markings
    isComplexError (field, row) {
      // Error marking for parcels that are not in destination country even though there are packages in destination country (left behind, maybe not arrived/scanned)
      if (field.name === constants.DASHBOARD.FIELD_NOT_IN_DESTINATION_COUNTRY && row[constants.DASHBOARD.FIELD_IN_DESTINATION_COUNTRY] > 0 && row[field.name] > 0) {
        return true
      }
      return false
    },
    elementSelected (entry, name) {
      // gets the filter that was clicked (null/undefined if not present)
      const fieldFilter = this.fieldFilters[name]
      this.openParcelList(fieldFilter, entry.Customer_ID, entry.Country_ID)
    },
    openParcelList: function (clickedStatusID, clickedCustomerID, clickedCountryID) {
      // Create the filter options needed in the parcel list
      var currentEventInfos = this.parcelFilter('dashboard').Current_Event_Infos.toString() !== '' ? this.parcelFilter('dashboard').Current_Event_Infos.toString() : '-1'
      var serviceLevelIds = this.parcelFilter('dashboard').Service_Levels.toString() !== '' ? this.parcelFilter('dashboard').Service_Levels.toString() : '-1'
      var tags = this.parcelFilter('dashboard').Tags.toString() !== '' ? this.parcelFilter('dashboard').Tags.toString() : '-1'
      var providers = this.parcelFilter('dashboard').Providers.toString() !== '' ? this.parcelFilter('dashboard').Providers.toString() : '-1'
      var fromPostalCode = this.parcelFilter('dashboard').From_Postal_Code !== '' ? this.parcelFilter('dashboard').From_Postal_Code : '-1'
      var toPostalCode = this.parcelFilter('dashboard').To_Postal_Code !== '' ? this.parcelFilter('dashboard').To_Postal_Code : '-1'
      var customers = clickedCustomerID
      var countries = clickedCountryID
      var statusIDs = clickedStatusID

      // If there was no country id passed we take the filter customer
      if (countries === '' || countries < 0) {
        if (Array.isArray(this.parcelFilter('dashboard').Countries) && this.parcelFilter('dashboard').Countries.length > 0) {
          countries = this.parcelFilter('dashboard').Countries.toString()
        } else {
          countries = '-1'
        }
      }
      // If there was no customer id passed we take the filter customer
      if (customers === '' || customers < 0) {
        if (Array.isArray(this.parcelFilter('dashboard').Customers) && this.parcelFilter('dashboard').Customers.length > 0) {
          customers = this.parcelFilter('dashboard').Customers.toString()
        } else {
          customers = '-1'
        }
      }

      // If there was no status id passed we take the filter statusIDs
      if (statusIDs === '' || statusIDs < 0) {
        if (Array.isArray(this.parcelFilter('dashboard').Parcel_Status) && this.parcelFilter('dashboard').Parcel_Status.length > 0) {
          statusIDs = this.parcelFilter('dashboard').Parcel_Status.toString()
        } else {
          statusIDs = '-1'
        }
      }

      // Filter Options passed to router
      var selectedOptions = {
        from_date: this.parcelFilter('dashboard').From_Date,
        to_date: this.parcelFilter('dashboard').To_Date,
        customer_id: customers,
        country_id: countries,
        service_level_id: serviceLevelIds,
        status: statusIDs,
        current_event_infos: currentEventInfos,
        show_forward_flow: this.parcelFilter('dashboard').Show_Forward_Flow,
        from_postal_code: fromPostalCode,
        to_postal_code: toPostalCode,
        providers: providers,
        tags: tags
      }
      this.$router.push({ name: 'parcel-list', params: selectedOptions })
    },

    // Toggles the sorting of the table asc, desc and then off
    toggleSort (field) {
      // Finds the sorted field in the array
      var sortedField = find(this.sortedFields, { field: field })
      if (sortedField) {
        // Switches to desc when asc already selected
        if (sortedField.direction === 'asc') {
          sortedField.direction = 'desc'
        } else {
          // removes the sorting when desc was selected
          this.sortedFields.splice(findIndex(this.sortedFields, { field: field }))
        }
      } else {
        // adds the field when not yet present
        this.sortedFields.push({ direction: 'asc', field: field })
      }
      this.sortData()
    }
  }
}
</script>

<style lang="scss">
  @import '../../variables.scss';
  .spl-dashboard-table {
    padding: 0 70px;
    overflow-y: scroll;
    height: calc(100vh - 235px);
    width: calc(100% - 10px);

    table {
       border-spacing: 0;
       border: 0;
    }
    &__field {
      &--number {
        text-align: right;
        width: 70px;
      }
      &--warning {
        border-bottom: 2px solid $blue !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $blue;
      }
      &--error {
        border-bottom: 2px solid $red !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $red;
      }
      &--empty:hover {
        background-color: $tableRowBackgroundColorHover !important;
      }
    }
    thead {
      th {
        position: sticky;
        top: 0;
        border: 0;
        text-align: left;
        background-color: $tableHeaderBackgroundColor;
        padding: 0px 5px  0px 5px;
        height: 40px;
        font-size: 13px;
        font-weight: 600;
        border-bottom: 1px solid $tableHeaderBorderColor;
      }

      th:hover {
        background-color: $tableColumnBackgroundColorHover;
        cursor: pointer;
      }
    }
    tbody {

      tr {
        background-color: $tableRowBackgroundColor;
      }

      tr:hover {
        background-color: $tableRowBackgroundColorHover;
      }

      td {
        border-style: solid;
        border-width: 0 1px 1px 0;
        border-color: $tableRowBorderLowColor;
        border-bottom-color: $tableRowBorderColor;
        font-size: 12px;
        padding: 5px;
        padding-top: 8px;
      }

      td:hover {
        background-color: $tableColumnBackgroundColorHover;
        cursor: pointer;
      }

      td:first-child {
        border-left-width: 1px;
      }
    }
  }

  @include mobile() {
    .spl-dashboard-table {
      padding: 0 5px 100px 5px;
      width: calc(100%);
      height: auto;
      overflow-y: auto;
    }
  }

</style>
